/**
 * La barra flotante de herramientas: una sola pieza de DOM que el canvas monta
 * encima de sí mismo.
 *
 * Vive acá y no en `CanvasView` porque no necesita nada de él: se comunica por
 * callbacks (qué herramienta, qué color, qué forma, y qué alineación, fondo y
 * color de texto se eligieron) y por un `syncOptions` que le dice qué opciones
 * importan ahora mismo.
 *
 * La barra es UN panel con dos filas. Abajo, siempre, las herramientas: nunca
 * se mueven, así el músculo aprende dónde está cada una. Arriba, y sólo cuando
 * hacen falta, las opciones de lo que se va a crear (formas, estilo de texto y
 * colores) en una única fila. Antes eran dos paneles flotantes independientes que se tapaban
 * entre sí; siendo un solo panel eso no puede volver a pasar.
 */

import { morphToolbar } from "../app/toolbarMorph.ts";
import { type ShapeKind, type TextAlign } from "../model/types.ts";
import {
  DEFAULT_FROM_END,
  DEFAULT_ROUTE,
  DEFAULT_TO_END,
  TEXT_BG_NONE,
  TEXT_BG_WHITE,
  TEXT_COLOR_DEFAULT,
  type EdgeEnd,
  type EdgeRoute,
} from "../model/types.ts";

/** Las herramientas de la barra. La define el canvas; acá sólo se dibujan. */
export type Tool =
  | "select"
  | "text"
  | "pan"
  | "draw"
  | "comment"
  | "container"
  | "connector"
  | "shape";

/**
 * Las herramientas que tienen color propio. Cada una recuerda el suyo: elegir
 * rojo con el lápiz no cambia el de la forma, el del conector ni el del
 * contenedor. Antes había un solo `activeColor` para las cuatro y cambiarlo en
 * una lo cambiaba en todas.
 */
export type ColorTool = "draw" | "shape" | "connector" | "container";

/** De cada herramienta al color que recuerda (`null` = no tiene color). */
function colorSlotOf(tool: Tool): ColorTool | null {
  switch (tool) {
    case "draw":
    case "shape":
    case "connector":
    case "container":
      return tool;
    default:
      return null;
  }
}

const DEFAULT_TOOL_COLOR = "#343331";
const TOOLBAR_PREFS_KEY = "notespace:toolbar-prefs";

interface ToolbarPrefs {
  colors?: Partial<Record<ColorTool, string>>;
  align?: TextAlign;
  shapeAlign?: TextAlign;
  textBg?: string;
  textColor?: string;
  shape?: ShapeKind;
  route?: EdgeRoute;
  ends?: { from: EdgeEnd; to: EdgeEnd };
  commentPinned?: boolean;
  commentBodyAutoScale?: boolean;
  containerTitleAutoScale?: boolean;
}

/** Preferencias guardadas (una por herramienta). Si el storage falla, se usan los valores por defecto. */
function loadToolbarPrefs(): ToolbarPrefs {
  try {
    const raw = localStorage.getItem(TOOLBAR_PREFS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as ToolbarPrefs;
  } catch {
    return {};
  }
}

/** El color guardado vale sólo si sigue estando en la paleta. */
function validPaletteColor(hex: unknown, palette: readonly string[]): string | null {
  return typeof hex === "string" && (palette as readonly string[]).includes(hex) ? hex : null;
}

const SELECT_TOOL_SVG =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 3.5 20 11l-7 2-3 6.5z"/></svg>';
const TEXT_TOOL_SVG =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><polyline points="5 7 5 4 19 4 19 7"/><line x1="12" y1="4" x2="12" y2="20"/><line x1="9" y1="20" x2="15" y2="20"/></svg>';
// Tabler "hand-stop" icon (MIT), re-stroked to 1.7 to match the toolbar's line weight.
const PAN_TOOL_SVG =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M8 13v-7.5a1.5 1.5 0 0 1 3 0v6.5"/><path d="M11 5.5v-2a1.5 1.5 0 1 1 3 0v8.5"/><path d="M14 5.5a1.5 1.5 0 0 1 3 0v6.5"/><path d="M17 7.5a1.5 1.5 0 0 1 3 0v8.5a6 6 0 0 1-6 6h-2h.208a6 6 0 0 1-5.012-2.7 69.74 69.74 0 0 1-.196-.3c-.312-.479-1.407-2.388-3.286-5.728a1.5 1.5 0 0 1 .536-2.022 1.867 1.867 0 0 1 2.28.28l1.47 1.47"/></svg>';
// Tabler "pencil" icon (MIT), re-stroked to 1.7 to match the toolbar's line weight.
const DRAW_TOOL_SVG =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4"/><path d="M13.5 6.5l4 4"/></svg>';
const COMMENT_SVG =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M5.5 4.5h13a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H12l-5 3.5v-3.5H5.5a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2Z"/><path d="M8 9h8M8 12.5h5"/></svg>';
const COMMENT_PIN_SVG =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="m9 4 6 0-.8 5 2.8 3v1H7v-1l2.8-3z"/><path d="M12 13v7"/></svg>';
const COMMENT_AUTO_SCALE_SVG =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5"/><path d="m3 8 5-5M16 3l5 5M3 16l5 5M16 21l5-5"/><rect x="8" y="8" width="8" height="8" rx="2"/></svg>';
/* Título del contenedor a tamaño de pantalla: las cuatro líneas del frame
   (mismo gesto que el ícono de la herramienta) con una `A` adentro que no
   cambia de cuerpo. Es su propio ícono y no el del comentario porque las dos
   opciones pueden estar a la vista a la vez, con un contenedor y un mensaje
   seleccionados: dos botones iguales en la misma barra no se distinguen. */
const TITLE_AUTO_SCALE_SVG =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 3v18M18 3v18M3 6h18M3 18h18"/><path d="m9.4 15 2.6-6.4 2.6 6.4M10.3 12.8h3.4"/></svg>';
// Tabler "topology-star-3" recortado: dos nodos unidos por una línea.
const CONNECTOR_SVG =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="5.5" r="2.5"/><path d="M7.5 16.5 16.5 7.5"/></svg>';
/**
 * Los íconos de las formas van RELLENOS y no de contorno: una forma se crea con
 * relleno sólido y sin borde, así que un ícono de contorno prometería otra cosa.
 * El ícono es la previa de lo que sale.
 */
const SHAPE_SVG: Record<ShapeKind, string> = {
  rect: '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><rect x="3.5" y="6" width="17" height="12" rx="2"/></svg>',
  ellipse:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><ellipse cx="12" cy="12" rx="8.5" ry="6"/></svg>',
  diamond:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 3.5 20.5 12 12 20.5 3.5 12Z"/></svg>',
  pill: '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><rect x="3.5" y="6.5" width="17" height="11" rx="5.5"/></svg>',
};

const SHAPE_TITLES: Record<ShapeKind, string> = {
  rect: "Rectangle",
  ellipse: "Ellipse",
  diamond: "Diamond",
  pill: "Pill",
};

/** Atajo de cada forma. La píldora no tiene: es la menos usada de las cuatro. */
const SHAPE_KEYS: Partial<Record<ShapeKind, string>> = {
  rect: "R",
  ellipse: "O",
  diamond: "D",
};

/** El título de un botón de forma, con su atajo si lo tiene. */
function shapeTitle(kind: ShapeKind): string {
  const key = SHAPE_KEYS[kind];
  return key ? `${SHAPE_TITLES[kind]} (${key})` : SHAPE_TITLES[kind];
}

/**
 * Alineación horizontal del texto. Los íconos son las tres pilas de renglones
 * de siempre: dicen a qué borde se pegan las líneas sin necesidad de leerlos.
 */
const ALIGN_SVG: Record<TextAlign, string> = {
  left: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="14" y2="12"/><line x1="4" y1="18" x2="18" y2="18"/></svg>',
  center:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="5" y1="18" x2="19" y2="18"/></svg>',
  right:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="10" y1="12" x2="20" y2="12"/><line x1="6" y1="18" x2="20" y2="18"/></svg>',
};

const ALIGN_TITLES: Record<TextAlign, string> = {
  left: "Align left",
  center: "Align center",
  right: "Align right",
};

/**
 * Los dos fondos que puede tener un nodo de texto: ninguno y blanco.
 *
 * El de "ninguno" es el cuadrado tachado que usa cualquier editor para decir
 * "sin relleno"; el blanco se dibuja con borde porque, sin él, un cuadrado
 * blanco sobre la barra blanca no se vería.
 */
const TEXT_BG_SVG: Record<string, string> = {
  [TEXT_BG_NONE]:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><rect x="4.5" y="4.5" width="15" height="15" rx="3"/><line x1="6" y1="18" x2="18" y2="6"/></svg>',
  [TEXT_BG_WHITE]:
    '<svg viewBox="0 0 24 24" width="18" height="18"><rect x="4.5" y="4.5" width="15" height="15" rx="3" fill="#ffffff" stroke="currentColor" stroke-width="1.7"/></svg>',
};

const TEXT_BG_TITLES: Record<string, string> = {
  [TEXT_BG_NONE]: "No background",
  [TEXT_BG_WHITE]: "White background",
};

const CONTAINER_SVG =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="3" x2="9" y2="21"/></svg>';

/**
 * El orden de la barra es el orden de la cabeza: primero mirar (elegir y
 * moverse por el lienzo), después crear. La línea separa esos dos grupos.
 * Dentro de "crear", la forma va antes que el conector porque se dibujan las
 * cajas y recién después se unen.
 */
const TOOLS: ({ tool: Tool; icon: string; title: string } | "sep")[] = [
  { tool: "select", icon: SELECT_TOOL_SVG, title: "Select (V)" },
  { tool: "pan", icon: PAN_TOOL_SVG, title: "Hand (H)" },
  "sep",
  { tool: "text", icon: TEXT_TOOL_SVG, title: "Text (T)" },
  { tool: "comment", icon: COMMENT_SVG, title: "Comment (M)" },
  { tool: "draw", icon: DRAW_TOOL_SVG, title: "Pencil (P)" },
  { tool: "shape", icon: SHAPE_SVG.rect, title: shapeTitle("rect") },
  { tool: "connector", icon: CONNECTOR_SVG, title: "Connector (C)" },
  { tool: "container", icon: CONTAINER_SVG, title: "Frame (A)" },
];

export const COLORS = [
  "#0082F9",
  "#059316",
  "#343331",
  "#7C6EEF",
  "#DA3AA6",
  "#E04343",
] as const;

/**
 * Los colores que puede tener el texto: el negro de siempre y los de la paleta.
 *
 * El primero va al frente y no es uno más: es el color con el que nace un nodo
 * de texto, y sin él elegir un tono sería un viaje de ida —no habría cómo
 * volver al negro—. Los otros son los MISMOS de la paleta del lienzo a
 * propósito: un texto y la forma que lo acompaña se pintan del mismo color
 * eligiendo el mismo cuadrado.
 *
 * El único que no está es el casi-negro de la paleta: al lado del color por
 * defecto son dos cuadrados oscuros indistinguibles, y ofrecer dos veces lo
 * mismo sólo obliga a adivinar cuál de los dos es el que ya está puesto.
 */
export const TEXT_COLORS: readonly string[] = [
  TEXT_COLOR_DEFAULT,
  ...COLORS.filter((hex) => hex !== "#343331"),
];

/** Escribe `hidden` sólo si cambia: `syncOptions` corre una vez por frame. */
function setHidden(el: HTMLElement, hidden: boolean): void {
  if (el.hidden !== hidden) el.hidden = hidden;
}

export interface CanvasToolbarCallbacks {
  /** El usuario eligió una herramienta (botón o atajo). */
  onTool: (tool: Tool) => void;
  /** El usuario eligió un color de la paleta. */
  onColor: (hex: string) => void;
  /** El usuario eligió una forma de la fila de opciones. */
  onShape: (kind: ShapeKind) => void;
  /** El usuario eligió una alineación para el texto. */
  onTextAlign: (align: TextAlign) => void;
  /** El usuario eligió el fondo del texto (`TEXT_BG_NONE` o un hex). */
  onTextBackground: (background: string) => void;
  /** El usuario eligió el color de las letras. */
  onTextColor: (color: string) => void;
  /** Fija o vuelve flotante la burbuja del comentario. */
  onCommentPinned: (pinned: boolean) => void;
  /** Compensa el zoom únicamente en el cuerpo del comentario. */
  onCommentBodyAutoScale: (enabled: boolean) => void;
  /** Compensa el zoom en el título del contenedor (tamaño de pantalla). */
  onContainerTitleAutoScale: (enabled: boolean) => void;
}

export class CanvasToolbar {
  /** El panel entero: opciones arriba, herramientas abajo. */
  readonly root: HTMLElement;

  /**
   * Lo que muestra la paleta ahora mismo. Cuando la herramienta activa tiene
   * color propio, es el de esa herramienta; en `select` es el último elegido
   * (lo que se va a aplicar a la selección).
   */
  private activeColor = DEFAULT_TOOL_COLOR;
  /** El color recordado por cada herramienta que tiene uno. */
  private toolColors: Record<ColorTool, string> = {
    draw: DEFAULT_TOOL_COLOR,
    shape: DEFAULT_TOOL_COLOR,
    connector: DEFAULT_TOOL_COLOR,
    container: DEFAULT_TOOL_COLOR,
  };
  private activeShape: ShapeKind = "rect";
  /** Estilo con el que nace el próximo nodo de texto (lo último elegido), igual
   *  que `activeColor` para lo que tiene color. */
  private activeAlign: TextAlign = "left";
  /**
   * La alineación con la que nace la PRÓXIMA figura, aparte de la del texto
   * suelto y por el mismo motivo que cada herramienta recuerda su color: son
   * dos cosas distintas con dos valores por defecto distintos (un texto nace
   * pegado a la izquierda; una figura, centrada), y compartir uno solo haría
   * que elegir en una cambiara la otra.
   */
  private activeShapeAlign: TextAlign = "center";
  /** A quién le habla la fila de alineación ahora mismo: al texto suelto, a la
   *  figura, o a nadie (la fila está oculta). */
  private alignTarget: "none" | "text" | "shape" = "none";
  private activeTextBg: string = TEXT_BG_NONE;
  private activeTextColor: string = TEXT_COLOR_DEFAULT;
  private activeCommentPinned = false;
  private activeCommentBodyAutoScale = false;
  /**
   * Con qué tamaño de título nace el próximo contenedor. Por defecto no, o sea
   * el título es parte del recuadro y escala con él; activarlo se lo devuelve
   * al tamaño de pantalla, como el cuerpo de un comentario en auto.
   */
  private activeContainerTitleAutoScale = false;
  /** Lo último marcado en el DOM, para no reescribirlo en cada frame. */
  private appliedAlign: TextAlign | null = null;
  private appliedTextBg: string | null = null;
  private appliedTextColor: string | null = null;
  private appliedShape: ShapeKind | null = null;
  /** Último estilo de conector usado: ruta y puntas. Los conectores nuevos
   *  nacen con esto, igual que nacen con `activeColor`. */
  private activeRoute: EdgeRoute = DEFAULT_ROUTE;
  private activeFromEnd: EdgeEnd = DEFAULT_FROM_END;
  private activeToEnd: EdgeEnd = DEFAULT_TO_END;
  private activeTool: Tool = "select";
  private shapeButton!: HTMLElement;
  /** La fila de arriba y sus partes. */
  private options!: HTMLElement;
  private shapeRow!: HTMLElement;
  private alignRow!: HTMLElement;
  /** Las líneas que separan la alineación de lo que venga después. Cada una
   *  está sólo cuando hay algo a los dos lados: una línea al borde del panel,
   *  o entre una fila y el vacío, no separa nada. */
  private alignSep!: HTMLElement;
  private textSep!: HTMLElement;
  private textRow!: HTMLElement;
  private commentRow!: HTMLElement;
  private containerRow!: HTMLElement;
  private optionsSep!: HTMLElement;
  private colorRow!: HTMLElement;
  /** Últimos valores pedidos por `syncOptions`: si el color y si las opciones
   *  de texto importan ahora. */
  private wantColors = false;
  private wantText = false;
  private wantAlign = false;
  private wantComment = false;
  private wantContainer = false;
  /** Última combinación de filas ya aplicada al DOM (`renderOptions` corre por frame). */
  private appliedOptions: string | null = null;

  constructor(private cb: CanvasToolbarCallbacks) {
    this.restorePrefs();
    this.root = document.createElement("div");
    this.root.className = "canvas-toolbar";
    this.root.append(this.buildOptions(), this.buildTools());
    // Ahora que el botón de forma existe, el ícono se pone al día con la forma
    // recordada: si la última fue la elipse, la barra no puede abrir con el
    // rectángulo dibujado.
    this.setShape(this.activeShape);
  }

  /**
   * Recupera lo último elegido por herramienta (color de cada una, estilo del
   * texto, forma, ruta y puntas). Todo se valida contra las opciones reales:
   * un valor guardado que ya no exista se ignora y queda el de fábrica.
   */
  private restorePrefs(): void {
    const prefs = loadToolbarPrefs();
    if (prefs.colors) {
      (Object.keys(this.toolColors) as ColorTool[]).forEach((slot) => {
        const saved = validPaletteColor(prefs.colors?.[slot], COLORS);
        if (saved) this.toolColors[slot] = saved;
      });
    }
    if (prefs.align === "left" || prefs.align === "center" || prefs.align === "right") {
      this.activeAlign = prefs.align;
    }
    if (
      prefs.shapeAlign === "left" ||
      prefs.shapeAlign === "center" ||
      prefs.shapeAlign === "right"
    ) {
      this.activeShapeAlign = prefs.shapeAlign;
    }
    if (prefs.textBg === TEXT_BG_NONE || prefs.textBg === TEXT_BG_WHITE) {
      this.activeTextBg = prefs.textBg;
    }
    const savedTextColor = validPaletteColor(prefs.textColor, TEXT_COLORS);
    if (savedTextColor) this.activeTextColor = savedTextColor;
    if (
      prefs.shape === "rect" ||
      prefs.shape === "ellipse" ||
      prefs.shape === "diamond" ||
      prefs.shape === "pill"
    ) {
      this.activeShape = prefs.shape;
    }
    if (prefs.route === "straight" || prefs.route === "elbow" || prefs.route === "curved") {
      this.activeRoute = prefs.route;
    }
    if (prefs.ends) {
      const { from, to } = prefs.ends;
      const ends: readonly string[] = ["none", "arrow", "dot", "diamond"];
      if (typeof from === "string" && ends.includes(from) && typeof to === "string" && ends.includes(to)) {
        this.activeFromEnd = from as EdgeEnd;
        this.activeToEnd = to as EdgeEnd;
      }
    }
    if (typeof prefs.commentPinned === "boolean") {
      this.activeCommentPinned = prefs.commentPinned;
    }
    if (typeof prefs.commentBodyAutoScale === "boolean") {
      this.activeCommentBodyAutoScale = prefs.commentBodyAutoScale;
    }
    if (typeof prefs.containerTitleAutoScale === "boolean") {
      this.activeContainerTitleAutoScale = prefs.containerTitleAutoScale;
    }
    this.activeColor = this.toolColors.shape;
  }

  /** Guarda lo actual para que cada herramienta lo recupere al volver. */
  private persistPrefs(): void {
    try {
      const prefs: ToolbarPrefs = {
        colors: { ...this.toolColors },
        align: this.activeAlign,
        shapeAlign: this.activeShapeAlign,
        textBg: this.activeTextBg,
        textColor: this.activeTextColor,
        shape: this.activeShape,
        route: this.activeRoute,
        ends: { from: this.activeFromEnd, to: this.activeToEnd },
        commentPinned: this.activeCommentPinned,
        commentBodyAutoScale: this.activeCommentBodyAutoScale,
        containerTitleAutoScale: this.activeContainerTitleAutoScale,
      };
      localStorage.setItem(TOOLBAR_PREFS_KEY, JSON.stringify(prefs));
    } catch {
      // El storage puede fallar en modo privado: no vale un crash por recordar un color.
    }
  }

  /**
   * Fila de opciones (arriba). Se arma entera una sola vez y después sólo se
   * muestran u ocultan sus partes: nada se re-crea al cambiar de herramienta,
   * así que nada salta de lugar.
   */
  private buildOptions(): HTMLElement {
    this.options = document.createElement("div");
    this.options.className = "toolbar-options";
    this.options.hidden = true;

    this.shapeRow = document.createElement("div");
    this.shapeRow.className = "toolbar-row";
    this.shapeRow.hidden = true;
    for (const kind of Object.keys(SHAPE_SVG) as ShapeKind[]) {
      const btn = document.createElement("button");
      btn.className = "canvas-tool";
      btn.innerHTML = SHAPE_SVG[kind];
      btn.title = shapeTitle(kind);
      btn.dataset.shape = kind;
      btn.addEventListener("pointerdown", (e) => e.preventDefault());
      btn.addEventListener("click", () => {
        this.setShape(kind);
        this.cb.onShape(kind);
      });
      this.shapeRow.appendChild(btn);
    }
    this.setShape(this.activeShape);

    // La alineación tiene fila propia y no vive con el resto del estilo del
    // texto porque no le pertenece sólo a él: el texto adentro de una figura se
    // alinea igual, y la figura no tiene fondo ni color de letra que elegir.
    // Siendo una fila aparte, aparece con las dos herramientas sin arrastrar
    // opciones que ahí no querrían decir nada.
    this.alignRow = document.createElement("div");
    this.alignRow.className = "toolbar-row";
    this.alignRow.hidden = true;
    for (const align of Object.keys(ALIGN_SVG) as TextAlign[]) {
      const btn = document.createElement("button");
      btn.className = "canvas-tool";
      btn.innerHTML = ALIGN_SVG[align];
      btn.title = ALIGN_TITLES[align];
      btn.dataset.align = align;
      btn.addEventListener("pointerdown", (e) => e.preventDefault());
      btn.addEventListener("click", () => {
        // Queda recordada para lo que la fila esté mirando —el texto suelto o
        // la figura— y se aplica a lo seleccionado, sea de uno u otro tipo.
        if (this.alignTarget === "shape") this.setShapeAlign(align);
        else this.setAlign(align);
        this.cb.onTextAlign(align);
      });
      this.alignRow.appendChild(btn);
    }

    // Las opciones que son sólo del texto suelto: los dos fondos y los colores,
    // separados entre sí porque son dos preguntas distintas sobre la misma caja.
    this.textRow = document.createElement("div");
    this.textRow.className = "toolbar-row";
    this.textRow.hidden = true;
    for (const bg of [TEXT_BG_NONE, TEXT_BG_WHITE]) {
      const btn = document.createElement("button");
      btn.className = "canvas-tool";
      btn.innerHTML = TEXT_BG_SVG[bg];
      btn.title = TEXT_BG_TITLES[bg];
      btn.dataset.bg = bg;
      btn.addEventListener("pointerdown", (e) => e.preventDefault());
      btn.addEventListener("click", () => {
        this.setTextBackground(bg);
        this.cb.onTextBackground(bg);
      });
      this.textRow.appendChild(btn);
    }

    // Y el color de las letras: la otra pregunta sobre la misma caja, con su
    // propia línea al lado. Son los mismos cuadrados de la paleta del lienzo
    // —la clase es la misma— pero viven acá y no en la fila de colores porque
    // esa fila pinta lo seleccionado (formas, trazos, conectores) y ésta el
    // texto: mezclarlas obligaría a adivinar a cuál de las dos cosas apunta un
    // click cuando hay de las dos seleccionadas.
    const colorSep = document.createElement("div");
    colorSep.className = "toolbar-sep";
    this.textRow.appendChild(colorSep);
    for (const hex of TEXT_COLORS) {
      const swatch = document.createElement("button");
      swatch.className = "color-swatch";
      swatch.dataset.textColor = hex;
      swatch.style.background = hex;
      swatch.title = hex === TEXT_COLOR_DEFAULT ? "Default text color" : hex;
      swatch.addEventListener("pointerdown", (e) => e.preventDefault());
      swatch.addEventListener("click", () => {
        this.setTextColor(hex);
        this.cb.onTextColor(hex);
      });
      this.textRow.appendChild(swatch);
    }

    this.refreshAlignHighlight();
    this.setTextBackground(this.activeTextBg);
    this.setTextColor(this.activeTextColor);

    this.commentRow = document.createElement("div");
    this.commentRow.className = "toolbar-row";
    this.commentRow.hidden = true;
    const pin = document.createElement("button");
    pin.className = "canvas-tool";
    pin.innerHTML = COMMENT_PIN_SVG;
    pin.title = "Always show comment";
    pin.dataset.commentOption = "pinned";
    pin.addEventListener("pointerdown", (e) => e.preventDefault());
    pin.addEventListener("click", () => {
      this.setCommentPinned(!this.activeCommentPinned);
      this.cb.onCommentPinned(this.activeCommentPinned);
    });
    const autoScale = document.createElement("button");
    autoScale.className = "canvas-tool";
    autoScale.innerHTML = COMMENT_AUTO_SCALE_SVG;
    autoScale.title = "Auto-scale comment body";
    autoScale.dataset.commentOption = "body-auto-scale";
    autoScale.addEventListener("pointerdown", (e) => e.preventDefault());
    autoScale.addEventListener("click", () => {
      this.setCommentBodyAutoScale(!this.activeCommentBodyAutoScale);
      this.cb.onCommentBodyAutoScale(this.activeCommentBodyAutoScale);
    });
    this.commentRow.append(pin, autoScale);
    this.refreshCommentHighlights();

    // La única opción propia del contenedor que no es color: si su título
    // acompaña al recuadro (por defecto) o se queda a tamaño de pantalla.
    this.containerRow = document.createElement("div");
    this.containerRow.className = "toolbar-row";
    this.containerRow.hidden = true;
    const titleScale = document.createElement("button");
    titleScale.className = "canvas-tool";
    titleScale.innerHTML = TITLE_AUTO_SCALE_SVG;
    titleScale.title = "Keep frame title at screen size";
    titleScale.dataset.containerOption = "title-auto-scale";
    titleScale.addEventListener("pointerdown", (e) => e.preventDefault());
    titleScale.addEventListener("click", () => {
      this.setContainerTitleAutoScale(!this.activeContainerTitleAutoScale);
      this.cb.onContainerTitleAutoScale(this.activeContainerTitleAutoScale);
    });
    this.containerRow.append(titleScale);
    this.refreshContainerHighlights();

    this.optionsSep = document.createElement("div");
    this.optionsSep.className = "toolbar-sep";
    this.optionsSep.hidden = true;

    this.colorRow = document.createElement("div");
    this.colorRow.className = "toolbar-row toolbar-colors";
    this.colorRow.hidden = true;
    for (const hex of COLORS) {
      const swatch = document.createElement("button");
      swatch.className = "color-swatch";
      swatch.dataset.color = hex;
      swatch.style.background = hex;
      swatch.title = hex;
      swatch.addEventListener("pointerdown", (e) => e.preventDefault());
      swatch.addEventListener("click", () => {
        this.setColor(hex);
        this.cb.onColor(hex);
      });
      if (hex === this.activeColor) swatch.classList.add("active");
      this.colorRow.appendChild(swatch);
    }

    this.alignSep = document.createElement("div");
    this.alignSep.className = "toolbar-sep";
    this.alignSep.hidden = true;
    this.textSep = document.createElement("div");
    this.textSep.className = "toolbar-sep";
    this.textSep.hidden = true;

    // El orden de la fila va de lo general a lo particular: primero cómo se
    // alinea el texto, después qué figura se dibuja, después el estilo del
    // texto suelto, y al final los colores detrás de su línea. Las formas
    // quedan en el medio del panel, no pegadas al borde: es lo que se elige
    // más seguido y está donde cae la vista.
    this.options.append(
      this.alignRow,
      this.alignSep,
      this.shapeRow,
      this.textSep,
      this.textRow,
      this.commentRow,
      this.containerRow,
      this.optionsSep,
      this.colorRow,
    );
    return this.options;
  }

  /** Fila de herramientas (abajo). Es la que nunca cambia de tamaño ni de sitio. */
  private buildTools(): HTMLElement {
    const row = document.createElement("div");
    row.className = "toolbar-row";
    for (const t of TOOLS) {
      if (t === "sep") {
        const sep = document.createElement("div");
        sep.className = "toolbar-sep";
        row.appendChild(sep);
        continue;
      }
      const btn = document.createElement("button");
      btn.className = "canvas-tool";
      btn.innerHTML = t.icon;
      btn.title = t.title;
      btn.dataset.tool = t.tool;
      btn.addEventListener("pointerdown", (e) => e.preventDefault());
      btn.addEventListener("click", () => this.cb.onTool(t.tool));
      if (t.tool === "shape") this.shapeButton = btn;
      row.appendChild(btn);
    }
    return row;
  }

  /** Lo que muestra la paleta: el color de la herramienta activa, o el último elegido en `select`. */
  get color(): string {
    return this.activeColor;
  }

  /** El color recordado por una herramienta concreta, sin importar cuál esté activa. */
  colorFor(tool: ColorTool): string {
    return this.toolColors[tool];
  }

  /**
   * Recuerda un color para una herramienta concreta: el de lo que se acaba de
   * repintar, o el de lo que se acaba de seleccionar.
   *
   * La paleta lo muestra si esa herramienta es la activa, y también cuando no
   * hay ninguna con color tomada (`select`): ahí el anillo apunta a lo
   * seleccionado. Si no, sólo queda guardado para cuando se vuelva a ella.
   *
   * Un color que no está en la paleta —de un archivo escrito a mano, o de una
   * versión con otros tonos— se muestra pero no se guarda: ningún cuadrado
   * queda marcado, que es la verdad, y la herramienta conserva el suyo.
   */
  setColorFor(tool: ColorTool, hex: string): void {
    const slot = colorSlotOf(this.activeTool);
    if ((slot === tool || slot === null) && this.activeColor !== hex) {
      this.activeColor = hex;
      this.refreshColorHighlight();
    }
    if (!validPaletteColor(hex, COLORS) || this.toolColors[tool] === hex) return;
    this.toolColors[tool] = hex;
    this.persistPrefs();
  }

  get shape(): ShapeKind {
    return this.activeShape;
  }

  /** Alineación con la que nace el próximo nodo de texto. */
  get align(): TextAlign {
    return this.activeAlign;
  }

  /** Alineación con la que nace la próxima figura. */
  get shapeAlign(): TextAlign {
    return this.activeShapeAlign;
  }

  /** Fondo con el que nace el próximo nodo de texto. */
  get textBackground(): string {
    return this.activeTextBg;
  }

  /** Color con el que nace el próximo nodo de texto. */
  get textColor(): string {
    return this.activeTextColor;
  }

  get commentPinned(): boolean {
    return this.activeCommentPinned;
  }

  get commentBodyAutoScale(): boolean {
    return this.activeCommentBodyAutoScale;
  }

  setCommentPinned(pinned: boolean): void {
    if (this.activeCommentPinned === pinned) return;
    this.activeCommentPinned = pinned;
    this.refreshCommentHighlights();
    this.persistPrefs();
  }

  setCommentBodyAutoScale(enabled: boolean): void {
    if (this.activeCommentBodyAutoScale === enabled) return;
    this.activeCommentBodyAutoScale = enabled;
    this.refreshCommentHighlights();
    this.persistPrefs();
  }

  get containerTitleAutoScale(): boolean {
    return this.activeContainerTitleAutoScale;
  }

  setContainerTitleAutoScale(enabled: boolean): void {
    if (this.activeContainerTitleAutoScale === enabled) return;
    this.activeContainerTitleAutoScale = enabled;
    this.refreshContainerHighlights();
    this.persistPrefs();
  }

  private refreshContainerHighlights(): void {
    if (!this.containerRow) return;
    for (const button of this.containerRow.querySelectorAll<HTMLElement>(
      "[data-container-option]",
    )) {
      button.classList.toggle("active", this.activeContainerTitleAutoScale);
      button.setAttribute("aria-pressed", String(this.activeContainerTitleAutoScale));
    }
  }

  private refreshCommentHighlights(): void {
    if (!this.commentRow) return;
    for (const button of this.commentRow.querySelectorAll<HTMLElement>("[data-comment-option]")) {
      const active =
        button.dataset.commentOption === "pinned"
          ? this.activeCommentPinned
          : this.activeCommentBodyAutoScale;
      button.classList.toggle("active", active);
      button.setAttribute("aria-pressed", String(active));
    }
  }

  /**
   * Marca la alineación activa (la elegida, o la del texto seleccionado).
   *
   * Las dos, como `syncOptions`, se llaman una vez por frame: sólo tocan el DOM
   * cuando el valor cambió de verdad.
   */
  setAlign(align: TextAlign): void {
    if (this.activeAlign === align) return;
    this.activeAlign = align;
    this.refreshAlignHighlight();
    this.persistPrefs();
  }

  /** Lo mismo para la figura: la elegida, o la de la figura seleccionada. */
  setShapeAlign(align: TextAlign): void {
    if (this.activeShapeAlign === align) return;
    this.activeShapeAlign = align;
    this.refreshAlignHighlight();
    this.persistPrefs();
  }

  /**
   * Marca la alineación de lo que la fila está mirando. Es UNA fila para dos
   * valores recordados, así que lo que se marca depende de a quién le habla: al
   * texto suelto o a la figura.
   */
  private refreshAlignHighlight(): void {
    if (!this.alignRow) return;
    const align = this.alignTarget === "shape" ? this.activeShapeAlign : this.activeAlign;
    if (this.appliedAlign === align) return;
    this.appliedAlign = align;
    for (const b of this.alignRow.querySelectorAll<HTMLElement>("[data-align]")) {
      b.classList.toggle("active", b.dataset.align === align);
    }
  }

  /** Marca el fondo activo (el elegido, o el del texto seleccionado). */
  setTextBackground(background: string): void {
    if (this.appliedTextBg === background) return;
    this.appliedTextBg = background;
    this.activeTextBg = background;
    for (const b of this.textRow.querySelectorAll<HTMLElement>("[data-bg]")) {
      b.classList.toggle("active", b.dataset.bg === background);
    }
    this.persistPrefs();
  }

  /**
   * Marca el color activo (el elegido, o el del texto seleccionado).
   *
   * Un texto pintado con un color que no está en la paleta —de un archivo
   * escrito a mano, o de una versión con otros tonos— no marca ninguno: el
   * anillo dice "es éste", y mentirlo sería peor que no decir nada.
   */
  setTextColor(color: string): void {
    if (this.appliedTextColor === color) return;
    this.appliedTextColor = color;
    this.activeTextColor = color;
    for (const b of this.textRow.querySelectorAll<HTMLElement>("[data-text-color]")) {
      b.classList.toggle("active", b.dataset.textColor === color);
    }
    this.persistPrefs();
  }

  /** Estilo con el que nace el próximo conector (lo último elegido). */
  get route(): EdgeRoute {
    return this.activeRoute;
  }

  get fromEnd(): EdgeEnd {
    return this.activeFromEnd;
  }

  get toEnd(): EdgeEnd {
    return this.activeToEnd;
  }

  /** Recuerda la ruta elegida para los próximos conectores. */
  setRoute(route: EdgeRoute): void {
    if (this.activeRoute === route) return;
    this.activeRoute = route;
    this.persistPrefs();
  }

  /** Recuerda las puntas elegidas para los próximos conectores. */
  setEdgeEnds(fromEnd: EdgeEnd, toEnd: EdgeEnd): void {
    if (this.activeFromEnd === fromEnd && this.activeToEnd === toEnd) return;
    this.activeFromEnd = fromEnd;
    this.activeToEnd = toEnd;
    this.persistPrefs();
  }

  /**
   * Recuerda la última forma usada —la elegida, o la de la forma seleccionada—
   * y la muestra en el botón de la barra.
   *
   * Como `setAlign`, se llama una vez por frame: sólo toca el DOM cuando la
   * forma cambió de verdad.
   */
  setShape(kind: ShapeKind): void {
    const changed = this.activeShape !== kind;
    this.activeShape = kind;
    if (this.appliedShape === kind) return;
    // `buildOptions` llama acá antes de que exista la fila de herramientas: el
    // ícono lo pinta el segundo llamado, ya desde el constructor.
    if (this.shapeButton) {
      this.appliedShape = kind;
      this.shapeButton.innerHTML = SHAPE_SVG[kind];
      // El título sigue a la forma activa: con el rombo elegido, el atajo que
      // hay que mostrar es `D` y no `R`.
      this.shapeButton.title = shapeTitle(kind);
    }
    for (const b of this.shapeRow.querySelectorAll<HTMLElement>(".canvas-tool")) {
      b.classList.toggle("active", b.dataset.shape === kind);
    }
    if (changed) this.persistPrefs();
  }

  /**
   * El usuario eligió un color en la paleta: se muestra y se recuerda para la
   * herramienta activa, sin tocar las demás. En `select` no hay herramienta
   * con color activa, así que sólo se muestra —quien llama (`recolorSelection`)
   * decide después a qué herramientas les queda guardado según lo repintado—.
   */
  private setColor(hex: string): void {
    this.activeColor = hex;
    const slot = colorSlotOf(this.activeTool);
    if (slot) this.toolColors[slot] = hex;
    this.refreshColorHighlight();
    this.persistPrefs();
  }

  private refreshColorHighlight(): void {
    if (!this.colorRow) return;
    for (const b of this.colorRow.querySelectorAll(".color-swatch")) {
      b.classList.toggle("active", (b as HTMLElement).dataset.color === this.activeColor);
    }
  }

  /** Marca cuál herramienta está activa y recupera el color que tenía guardado. */
  setTool(tool: Tool): void {
    this.activeTool = tool;
    const slot = colorSlotOf(tool);
    if (slot) {
      this.activeColor = this.toolColors[slot];
      this.refreshColorHighlight();
    }
    for (const btn of this.root.querySelectorAll<HTMLElement>(".canvas-tool")) {
      if (!btn.dataset.tool) continue; // los de forma no son herramientas
      btn.classList.toggle("active", btn.dataset.tool === tool);
    }
    this.renderOptions();
  }

  /**
   * Dice si el color importa ahora mismo. Se llama por frame, así que
   * `renderOptions` sólo toca el DOM cuando algo cambió.
   *
   * El color importa cuando se va a crear algo que lo tenga (lápiz, forma,
   * contenedor, conector) o cuando hay algo seleccionado que se pueda repintar.
   */
  syncOptions(
    showColors: boolean,
    showText: boolean,
    alignTarget: "none" | "text" | "shape",
    showComment = false,
    showContainer = false,
  ): void {
    this.wantColors = showColors;
    this.wantText = showText;
    this.wantAlign = alignTarget !== "none";
    this.wantComment = showComment;
    this.wantContainer = showContainer;
    if (alignTarget !== "none" && alignTarget !== this.alignTarget) {
      this.alignTarget = alignTarget;
      this.refreshAlignHighlight();
    }
    this.renderOptions();
  }

  /**
   * Las formas se ofrecen mientras la herramienta de forma esté tomada, sin
   * ningún segundo click escondido: si vas a dibujar una forma, ahí están las
   * cuatro. Y como las filas son partes del mismo panel, elegir forma, elegir
   * alineación y elegir color nunca compiten por el mismo lugar de la pantalla.
   */
  private renderOptions(): void {
    const shapes = this.activeTool === "shape";
    const left =
      shapes || this.wantAlign || this.wantText || this.wantComment || this.wantContainer;
    // Qué filas van a estar a la vista. Se compara contra lo último aplicado
    // porque `syncOptions` corre una vez por frame y medir la barra para
    // animarla cuesta un layout: si nada cambió, no se toca el DOM.
    const state = `${shapes}|${this.wantAlign}|${this.wantText}|${this.wantComment}|${this.wantContainer}|${this.wantColors}`;
    if (state === this.appliedOptions) return;
    this.appliedOptions = state;

    // Todo el cambio va junto adentro del morph: la barra mide una vez antes y
    // una vez después, y anima su caja entre los dos tamaños.
    morphToolbar(this.root, () => {
      // Las opciones del texto siguen la misma regla que las formas: están
      // mientras se vaya a escribir (herramienta tomada) o mientras haya un texto
      // seleccionado al que aplicárselas. La alineación, además, mientras se vaya
      // a dibujar una figura o haya una seleccionada: adentro también hay texto.
      setHidden(this.alignRow, !this.wantAlign);
      setHidden(this.shapeRow, !shapes);
      setHidden(this.textRow, !this.wantText);
      setHidden(this.commentRow, !this.wantComment);
      setHidden(this.containerRow, !this.wantContainer);
      // Cada línea sólo si tiene filas de los dos lados.
      setHidden(this.alignSep, !(this.wantAlign && shapes));
      setHidden(this.textSep, !((this.wantAlign || shapes) && this.wantText));
      setHidden(this.colorRow, !this.wantColors);
      setHidden(this.optionsSep, !(left && this.wantColors));
      setHidden(this.options, !left && !this.wantColors);
    });
  }

  destroy(): void {
    this.root.remove();
  }
}

/** Cursor en reposo de cada herramienta. */
export function toolCursor(tool: Tool): string {
  switch (tool) {
    case "text":
      return "text";
    case "pan":
      return "grab";
    case "draw":
    case "container":
    case "connector":
    case "shape":
    case "comment":
      return "crosshair";
    default:
      return "default";
  }
}
